import { useNavigate } from "react-router-dom";
import { Routes } from "router/routes";
import { FourArrows } from "assets/website";
import { ourServices1, ourServices2, ourServices3 } from "assets/images";
import { ChevronRight } from "lucide-react";

interface ServiceCard {
  title: string;
  description: string;
  image: string;
  imageAlt: string;
}

const services: ServiceCard[] = [
  {
    title: "Hiring Assistant",
    description:
      "Don't just say yes to a CV that looks good. Take your candidates for a test drive with our role-based assessments & find the right fit for your company.",
    image: ourServices1,
    imageAlt: "Hiring Assistant — role-based assessments",
  },
  {
    title: "Talent Management",
    description:
      'Your "bad" employee may just need a realignment. Our execution assessment will identify the gaps & help you fix what isn\'t working.',
    image: ourServices2,
    imageAlt: "Talent Management — execution assessment",
  },
  {
    title: "Competence Audit",
    description:
      "Beyond the KPIs, how well is your team really faring? Match expectation with execution with our appraisal assessments and get the best out of your teams.",
    image: ourServices3,
    imageAlt: "Competence Audit — appraisal assessments",
  },
];

const OurServicesSection = () => {
  const navigate = useNavigate();

  return (
    <section className="py-16 md:py-24 bg-[#F9FAFB]">
      <div className="container mx-auto xl:px-8 lg:px-4 px-4">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-10 md:mb-14">
          <div
            data-aos="fade-up"
            className="inline-flex py-2 items-center gap-2 mb-4 relative before:content-[''] before:absolute before:top-0 before:left-0 before:right-0 before:h-[1px] before:bg-[linear-gradient(90deg,#C6E0FF_0%,#000B55_100%)] after:content-[''] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[1px] after:bg-[linear-gradient(90deg,#C6E0FF_0%,#000B55_100%)]"
          >
            <FourArrows />
            <span className="sm:text-lg text-base text-[#050B17] tracking-wider">
              Solutions
            </span>
          </div>
          <h2
            data-aos="fade-up"
            data-aos-delay="100"
            className="text-2xl md:text-4xl lg:text-5xl font-display font-bold text-[#050B17] mb-4"
          >
            Our Services
          </h2>
          <p
            data-aos="fade-up"
            data-aos-delay="200"
            className="text-sm md:text-base lg:text-lg text-[#475467] !leading-[1.3]"
          >
            Behind every profitable organisation is a strong team. That's why
            we offer a curated suite of services to help you hire the right
            talent, assess your team's strengths and weaknesses, and close the
            gaps that matter most.
          </p>
        </div>

        {/* Service Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-20 mb-12">
          {services.map((service, index) => (
            <div
              key={index}
              data-aos="fade-up"
              data-aos-delay={100 + index * 100}
              className="flex flex-col group"
            >
              {/* Screenshot — slanted by default, straightens on hover */}
              <div
                style={{
                  maxWidth: "100%",
                  borderRadius: "8.1px",
                  boxShadow: "0px 3.6px 8.1px 1.8px #0000000D",
                  transform: "rotate(-10deg) skewY(12deg)",
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLImageElement).style.transform = "rotate(0deg) skewY(0deg)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLImageElement).style.transform = "rotate(-10deg) skewY(12deg)";
                }}
                className="shadow-[0px_3.6px_8.1px_1.8px_#0000000D] bg-white rounded-md sm:mx-0 mx-8 p-3.5 mb-6 flex items-center justify-center overflow-visible">
                <img
                  src={service.image}
                  alt={service.imageAlt}
                  className="object-cover transition-transform duration-300 ease-out"
                />
              </div>
              {/* Text */}
              <h3 className="mt-10 text-lg md:text-xl lg:text-2xl font-medium text-[#1D2939] mb-2 text-center">
                {service.title}
              </h3>
              <p className="text-sm md:text-base text-[#475467] leading-relaxed text-center">
                {service.description}
              </p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div
          data-aos="fade-up"
          data-aos-delay="400"
          className="text-center"
        >
          <button
            onClick={() => navigate(Routes.signup)}
            className="inline-flex items-center gap-1 px-6 py-3.5 bg-[#012169] hover:bg-[#011550] text-white font-medium rounded-lg transition-all duration-200 hover:shadow-lg shadow-[0px_4px_8px_0px_#01216940] text-sm md:text-[15px]"
          >
            Get Started
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

export { OurServicesSection };
